<template>
  <div class="view-container">
    <div v-if="loading" class="loading-state">
      <i class="fas fa-spinner fa-spin"></i>
      <span>加载任务详情中...</span>
    </div>

    <div v-else-if="error" class="error-state">
      <i class="fas fa-exclamation-circle"></i>
      <span>{{ error }}</span>
      <button class="btn btn-primary" @click="loadTaskDetail">重试</button>
    </div>

    <!-- 参考旧系统布局 -->
    <div v-else class="old-system-layout">
      <!-- 第一行: 任务信息栏 (单行显示) -->
      <div class="task-header-bar">
        <div class="task-title-row">
          <h2 class="task-title">{{ task.processName || '任务详情' }}</h2>
          <span class="task-status" :class="statusClass">{{ task.statusName || task.status || '-' }}</span>
          <button class="btn-print" @click="openPrint" title="打印"><i class="fas fa-print"></i> 打印</button>
        </div>
        <div class="task-meta-single-line">
          <span class="meta-item"><i class="fas fa-hashtag"></i> 任务号: {{ task.taskNo || '-' }}</span>
          <span class="meta-divider">|</span>
          <span class="meta-item"><i class="fas fa-file-alt"></i> 流水号: {{ task.serialNo || '-' }}</span>
          <span class="meta-divider">|</span>
          <span class="meta-item"><i class="fas fa-user-plus"></i> 发起人: {{ task.initiatorName || '-' }}</span>
          <span class="meta-divider">|</span>
          <span class="meta-item"><i class="fas fa-clock"></i> 发起时间: {{ fmtDate(task.initiateTime) }}</span>
          <template v-if="mode !== 'todo'">
            <span class="meta-divider">|</span>
            <span class="meta-item"><i class="fas fa-user-check"></i> 审批人: {{ task.approverName || '-' }}</span>
            <span class="meta-divider">|</span>
            <span class="meta-item"><i class="fas fa-calendar-check"></i> 处理时间: {{ fmtDate(task.operationTime) }}</span>
          </template>
        </div>
      </div>

      <!-- 第二行: 左右分栏布局 -->
      <div class="main-content-layout">
        <!-- 左侧: 表单内容区域 + 审批历史 -->
        <div class="left-panel">
          <!-- 表单区域 -->
          <div class="form-section">
            <div class="form-content no-header">
              <!-- 调试信息面板 (开发环境显示) -->
              <div v-if="false" class="debug-panel">
                <details>
                  <summary>🔍 表单加载调试信息</summary>
                  <div class="debug-content">
                    <p><strong>designerType:</strong> {{ designerType }}</p>
                    <p><strong>formControls.length:</strong> {{ formControls.length }}</p>
                    <p><strong>formContent存在:</strong> {{ !!formContent }}</p>
                    <p><strong>formContent长度:</strong> {{ formContent?.length || 0 }}</p>
                    <p><strong>formDataModel:</strong> {{ JSON.stringify(formDataModel) }}</p>
                  </div>
                </details>
              </div>

              <!-- 新设计器表单 -->
              <NewFormRenderer 
                v-if="designerType === '2' && formControls.length > 0"
                :controls="formControls"
                :form-data="formDataModel"
                :readonly="mode !== 'todo'"
                @update="onFormDataUpdate"
              />
              
              <!-- 旧设计器表单 -->
              <OldFormRenderer 
                v-else-if="designerType !== '2' && formContent"
                :html-content="formContent"
                :js-content="jsContent"
                :form-data="formDataModel"
                :readonly="mode !== 'todo'"
                :task-id="taskId"
                @update="onFormDataUpdate"
              />

              <div v-else class="empty-form-hint">
                <i class="fas fa-inbox"></i>
                <span>未配置表单</span>
                <small class="debug-hint">designerType: {{ designerType }}, formControls: {{ formControls.length }}, formContent: {{ formContent ? 'exists' : 'null' }}</small>
              </div>
            </div>
          </div>

          <!-- 审批历史区域 (可折叠) -->
          <div class="history-section">
            <div class="section-header collapsible" @click="historyExpanded = !historyExpanded">
              <div class="header-left">
                <i class="fas fa-history"></i>
                <span>审批历史</span>
                <span v-if="histories.length > 0" class="count-badge">{{ histories.length }}</span>
              </div>
              <i class="fas" :class="historyExpanded ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
            </div>
            <div v-show="historyExpanded" class="history-content">
              <div v-if="histories.length > 0" class="history-table-container">
                <table class="history-table">
                  <thead>
                    <tr>
                      <th style="width: 40px;">序号</th>
                      <th style="width: 120px;">处理步骤</th>
                      <th style="width: 100px;">处理人</th>
                      <th style="width: 100px;">处理动作</th>
                      <th style="width: 140px;">到达时间</th>
                      <th style="width: 140px;">审批时间</th>
                      <th style="width: 100px;">耗时</th>
                      <th>备注</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(h, i) in histories" :key="i">
                      <td class="text-center">{{ i + 1 }}</td>
                      <td>{{ h.nodeName || h.NodeName || '-' }}</td>
                      <td>{{ h.handlerName || h.HandlerName || '-' }}</td>
                      <td>{{ h.actionLinkName || h.ActionLinkName || '-' }}</td>
                      <td>{{ h.receiveOn || h.ReceiveOn || '-' }}</td>
                      <td>{{ h.finishOn || h.FinishOn || '-' }}</td>
                      <td class="text-center">-</td>
                      <td class="text-left">{{ h.memo || h.Memo || '-' }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div v-else class="empty-history">暂无审批历史</div>
            </div>
          </div>
        </div>

        <!-- 右侧: 操作面板 -->
        <div class="right-panel">
          <!-- 基本信息卡片 -->
          <div class="side-card">
            <div class="card-header">
              <i class="fas fa-info-circle"></i>
              <span>基本信息</span>
            </div>
            <div class="card-body">
              <div class="info-row">
                <span class="info-label">优先级:</span>
                <span class="priority-tag" :class="priorityClass">{{ priorityText }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">摘要:</span>
                <span class="info-value">{{ task.summary || '-' }}</span>
              </div>
            </div>
          </div>

          <!-- 审批操作卡片 (仅待办任务显示) -->
          <div v-if="mode === 'todo'" class="side-card">
            <div class="card-header">
              <i class="fas fa-tasks"></i>
              <span>审批操作</span>
            </div>
            <div class="card-body">
              <div class="form-group">
                <label class="form-label">审批意见:</label>
                <textarea 
                  v-model="comment" 
                  class="form-textarea" 
                  rows="3" 
                  placeholder="请输入审批意见..."
                ></textarea>
              </div>
              <div class="action-buttons-grid">
                <button 
                  v-for="btn in buttonConfigs" 
                  :key="btn.id"
                  @click="handleButtonClick(btn)" 
                  :class="['btn-action', 'btn-' + (btn.type || 'primary')]" 
                  :disabled="submitting"
                >
                  <i v-if="submitting && submittingActionId === btn.id" class="fas fa-spinner fa-spin"></i>
                  <i v-else :class="getButtonIcon(btn)"></i>
                  <span>{{ submitting && submittingActionId === btn.id ? '处理中...' : (btn.name || btn.actionLinkName) }}</span>
                </button>
              </div>
            </div>
          </div>

          <!-- 已处理任务操作卡片 -->
          <div v-if="mode === 'processed'" class="side-card">
            <div class="card-header">
              <i class="fas fa-undo"></i>
              <span>操作</span>
            </div>
            <div class="card-body">
              <button @click="doRetrieve" class="btn-action btn-save btn-full" :disabled="submitting">
                <i v-if="submitting" class="fas fa-spinner fa-spin"></i>
                <i v-else class="fas fa-redo"></i>
                {{ submitting ? '取回中...' : '取回' }}
              </button>
            </div>
          </div>

          <!-- 流程文档 (流程信息/新增修改时上传) -->
          <div class="side-card">
            <div class="card-header">
              <i class="fas fa-paperclip"></i>
              <span>流程文档</span>
            </div>
            <div class="card-body">
              <template v-if="flowDocs.length > 0">
                <div class="doc-item" v-for="doc in flowDocs" :key="doc.Guid || doc.guid">
                  <i :class="['fas', getDocIcon(doc.FileType || doc.fileType)]" style="color:#1890ff;margin-right:6px;"></i>
                  <a :href="getAttachDownloadUrl(doc.Guid || doc.guid)" target="_blank" class="doc-link">{{ doc.DspName || doc.dspName }}</a>
                  <span class="doc-size">{{ formatDocSize(doc.FileSize || doc.fileSize) }}</span>
                </div>
              </template>
              <div v-else class="empty-tip">暂无流程文档</div>
            </div>
          </div>

          <!-- 操作文档 (流程设计节点配置时上传) -->
          <div class="side-card">
            <div class="card-header">
              <i class="fas fa-file-upload"></i>
              <span>操作文档</span>
            </div>
            <div class="card-body">
              <template v-if="nodeDocs.length > 0">
                <div class="doc-item" v-for="doc in nodeDocs" :key="doc.Guid || doc.guid">
                  <i :class="['fas', getDocIcon(doc.FileType || doc.fileType)]" style="color:#52c41a;margin-right:6px;"></i>
                  <a :href="getAttachDownloadUrl(doc.Guid || doc.guid)" target="_blank" class="doc-link">{{ doc.DspName || doc.dspName }}</a>
                  <span class="doc-size">{{ formatDocSize(doc.FileSize || doc.fileSize) }}</span>
                </div>
              </template>
              <div v-else class="empty-tip">暂无操作文档</div>
            </div>
          </div>

          <!-- 节点说明卡片 -->
          <div class="side-card">
            <div class="card-header">
              <i class="fas fa-info-circle"></i>
              <span>节点说明</span>
            </div>
            <div class="card-body">
              <div class="node-description">
                {{ task.nodeDescription || '暂无节点说明' }}
              </div>
            </div>
          </div>

          <!-- 审批路线卡片 -->
          <div class="side-card">
            <div class="card-header">
              <i class="fas fa-route"></i>
              <span>审批路线</span>
            </div>
            <div class="card-body">
              <div v-if="approvalRoute && approvalRoute.length > 0" class="route-list">
                <div v-for="(node, index) in approvalRoute" :key="index" class="route-node">
                  <div class="route-node-header">
                    <span class="route-node-name">{{ node.nodeName || node.NodeName || '-' }}</span>
                    <span v-if="node.isCurrent" class="route-current-badge">当前</span>
                  </div>
                  <div class="route-node-users">
                    {{ node.approvers || node.Approvers || '待分配' }}
                  </div>
                </div>
              </div>
              <div v-else class="empty-tip">暂无路线信息</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 转办对话框 -->
    <div v-if="showForwardDialog" class="modal-overlay" @click.self="showForwardDialog = false">
      <div class="forward-dialog">
        <div class="dialog-header">
          <span>转办 - 选择处理人</span>
          <button class="dialog-close" @click="showForwardDialog = false">&times;</button>
        </div>
        <div class="dialog-body">
          <input 
            v-model="forwardSearch" 
            class="forward-search-input" 
            placeholder="搜索姓名或账号..."
            autofocus
          />
          <div class="forward-user-list">
            <div 
              v-for="user in filteredUsers" 
              :key="user.Account || user.account"
              class="forward-user-item"
              :class="{ selected: forwardSelectedUser && (forwardSelectedUser.Account || forwardSelectedUser.account) === (user.Account || user.account) }"
              @click="forwardSelectedUser = user"
            >
              <span class="forward-user-name">{{ user.DisplayName || user.displayName }}</span>
              <span class="forward-user-account">{{ user.Account || user.account }}</span>
            </div>
            <div v-if="filteredUsers.length === 0" class="forward-empty">无匹配用户</div>
          </div>
        </div>
        <div class="dialog-footer">
          <button class="btn-cancel" @click="showForwardDialog = false">取消</button>
          <button class="btn-submit" @click="doForwardSubmit" :disabled="!forwardSelectedUser || forwardSubmitting">
            <i v-if="forwardSubmitting" class="fas fa-spinner fa-spin"></i>
            {{ forwardSubmitting ? '转办中...' : '确认转办' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { getTodoTask, getAlreadyProcessed, buildPagedRequest, getTaskDetail, getTaskHistory, getApprovalRoute, getFormBasicInfo, approveTask, submitTask, forwardTask, retrieveTask, getButtonConfig } from '../api/task'
import { getFormById } from '../api/form'
import { getAttachList, getAttachDownloadUrl } from '../api/attach'
import { getUserList } from '../api/user'
import NewFormRenderer from '../components/NewFormRenderer.vue'
import OldFormRenderer from '../components/OldFormRenderer.vue'

const router = useRouter()

// 通过 props 接收当前 tab 的 payload（支持多 tab 独立数据）
const props = defineProps({
  tabPayload: {
    type: Object,
    default: () => ({})
  }
})

// 使用 computed 让 taskId 等随 tab 切换而更新
const taskId = computed(() => props.tabPayload?.taskId || props.tabPayload?.taskData?.taskId || '')
const mode = computed(() => props.tabPayload?.mode || 'view')
const processName = computed(() => props.tabPayload?.processName || '')
const preloadedTaskData = computed(() => props.tabPayload?.taskData || null)

const loading = ref(false)
const submitting = ref(false)
const error = ref('')
const actionType = ref('') // 'approve' | 'reject' | 'save'
const submittingActionId = ref(0) // 当前正在提交的按钮ID

const task = ref({})
const currentStepId = ref(0) // 当前用户的活跃步骤ID
const histories = ref([])
const approvalRoute = ref([]) // 审批路线
const comment = ref('')

const designerType = ref('2')

// 转办对话框
const showForwardDialog = ref(false)
const forwardSearch = ref('')
const allUsers = ref([])
const forwardSelectedUser = ref(null)
const forwardSubmitting = ref(false)
const formContent = ref('')
const jsContent = ref('')
const formControls = ref([])
const formDataModel = reactive({})
const flowDocs = ref([])    // 流程文档
const nodeDocs = ref([])    // 操作文档
const buttonConfigs = ref([]) // 操作按钮配置（从 SYSBPMDLINK 表读取）

// 计算属性
const statusClass = computed(() => {
  const s = (task.value.statusName || task.value.status || '').toLowerCase()
  if (s.includes('审批') || s.includes('处理') || s.includes('pending')) return 'status-processing'
  if (s.includes('完成') || s.includes('通过') || s.includes('同意') || s.includes('approved')) return 'status-done'
  if (s.includes('拒绝') || s.includes('退回') || s.includes('rejected')) return 'status-rejected'
  return ''
})

const priorityText = computed(() => {
  const p = task.value.priority || 0
  if (p >= 5) return '高'
  if (p >= 3) return '中'
  return '低'
})

const priorityClass = computed(() => {
  const p = task.value.priority || 0
  if (p >= 5) return 'priority-high'
  if (p >= 3) return 'priority-medium'
  return 'priority-low'
})

// 审批历史展开状态（默认折叠）
const historyExpanded = ref(false)

// 格式化日期
const fmtDate = (val) => {
  if (!val) return '-'
  const d = new Date(val)
  if (isNaN(d.getTime())) return val
  return d.toLocaleString('zh-CN')
}

// 加载流程文档和操作文档
const loadAttachDocs = async (processGuid, nodeId) => {
  flowDocs.value = []
  nodeDocs.value = []
  
  // 加载流程文档 (SavePath='Flow', FromGuid=processGuid)
  if (processGuid) {
    try {
      const res = await getAttachList('Flow', processGuid)
      const data = res.data || res.Data || res
      flowDocs.value = (data && (data.data || data.Data || data)) || []
    } catch (e) {
      console.warn('[TaskView] 加载流程文档失败:', e)
    }
  }
  
  // 加载操作文档 (SavePath='Node', FromGuid=nodeId)
  if (nodeId && nodeId > 0) {
    try {
      const res = await getAttachList('Node', String(nodeId))
      const data = res.data || res.Data || res
      nodeDocs.value = (data && (data.data || data.Data || data)) || []
    } catch (e) {
      console.warn('[TaskView] 加载操作文档失败:', e)
    }
  }
}

// 文件图标映射（纯文本，不需要 emoji，用于 template）
const getDocIcon = (ext) => {
  const map = {
    '.pdf': 'fa-file-pdf', '.doc': 'fa-file-word', '.docx': 'fa-file-word',
    '.xls': 'fa-file-excel', '.xlsx': 'fa-file-excel', '.ppt': 'fa-file-powerpoint',
    '.pptx': 'fa-file-powerpoint', '.jpg': 'fa-file-image', '.jpeg': 'fa-file-image',
    '.png': 'fa-file-image', '.gif': 'fa-file-image', '.zip': 'fa-file-archive',
    '.rar': 'fa-file-archive', '.7z': 'fa-file-archive', '.txt': 'fa-file-alt'
  }
  return map[ext?.toLowerCase()] || 'fa-file'
}

const formatDocSize = (bytes) => {
  if (!bytes || bytes === 0) return ''
  if (bytes < 1024) return bytes + ' B'
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
}

const loadTaskDetail = async () => {
  if (!taskId.value) {
    error.value = '任务ID不存在'
    return
  }
  
  loading.value = true
  error.value = ''
  
  try {
    let taskData = null
    
    // 1. 优先使用预加载的任务数据
    if (preloadedTaskData.value) {
      console.log('[TaskView] 使用预加载的任务数据:', preloadedTaskData.value)
      taskData = preloadedTaskData.value
    }
    
    // 2. 如果没有预加载数据，尝试从各个任务列表获取
    if (!taskData) {
      // 定义所有可能的任务列表API
      const taskListAPIs = [
        { name: '待办列表', api: getTodoTask, condition: mode.value === 'todo' || mode.value === 'view' },
        { name: '已处理列表', api: getAlreadyProcessed, condition: mode.value === 'processed' || mode.value === 'view' },
        { name: '可查看列表', api: () => import('../api/task').then(m => m.getAllowViewTask(buildPagedRequest({ pageNumber: 0, pageSize: 500 }))), condition: mode.value === 'view' },
        { name: '已发起列表', api: () => import('../api/task').then(m => m.getAlreadyInitiated(buildPagedRequest({ pageNumber: 0, pageSize: 500 }))), condition: mode.value === 'view' },
        { name: '抄送列表', api: () => import('../api/task').then(m => m.getAlreadyCC(buildPagedRequest({ pageNumber: 0, pageSize: 500 }))), condition: mode.value === 'view' },
        { name: '共享列表', api: () => import('../api/task').then(m => m.getShareTask(buildPagedRequest({ pageNumber: 0, pageSize: 500 }))), condition: mode.value === 'view' }
      ]
      
      // 按优先级尝试从各个列表获取任务数据
      for (const { name, api, condition } of taskListAPIs) {
        if (!condition) continue
        
        try {
          console.log(`[TaskView] 从${name}获取任务...`)
          const res = await (typeof api === 'function' ? api(buildPagedRequest({ pageNumber: 0, pageSize: 500 })) : api)
          const tasks = res.rows || res.Rows || []
          taskData = tasks.find(t => 
            (t.taskId || t.TaskId) === taskId.value || 
            String(t.taskId || t.TaskId) === String(taskId.value)
          )
          
          if (taskData) {
            console.log(`[TaskView] 从${name}找到任务:`, taskData)
            break // 找到就停止搜索
          }
        } catch (e) {
          console.warn(`[TaskView] 从${name}获取任务失败:`, e)
        }
      }
    }
    
    // 3. 如果还是没找到，尝试调用 getTaskDetail API
    if (!taskData) {
      try {
        console.log('[TaskView] 调用 getTaskDetail API...')
        const taskRes = await getTaskDetail(taskId.value)
        const raw = taskRes.data || taskRes.Data || taskRes
        taskData = raw?.Data || raw  // 解包 { ResultType, Data }
        console.log('[TaskView] getTaskDetail API 返回:', taskData)
      } catch (detailError) {
        console.warn('[TaskView] getTaskDetail API 失败:', detailError)
      }
    }
    
    // 确保获取到了任务数据
    if (!taskData) {
      throw new Error('未能从任何数据源获取到任务详情，请确认任务ID是否正确')
    }
    
    // 始终调用 getTaskDetail 获取完整数据（包括表单数据）
    try {
      console.log('[TaskView] 调用 getTaskDetail 获取完整数据...')
      const detailRes = await getTaskDetail(taskId.value)
      const rawDetail = detailRes.data || detailRes.Data || detailRes
      const detailData = rawDetail?.Data || rawDetail  // 解包 { ResultType, Data }
      if (detailData) {
        // 保存预加载数据中可能存在的有效 stepId（getTaskDetail 可能返回 0）
        const preStepId = taskData.stepId || taskData.StepId || 0
        // 合并数据：优先使用 detailData 的字段
        taskData = { ...taskData, ...detailData }
        // 若 getTaskDetail 返回 stepId=0，回退到预加载数据中的 stepId
        if ((!taskData.stepId || taskData.stepId === 0) && preStepId) {
          taskData.stepId = preStepId
          console.log('[TaskView] getTaskDetail stepId 为 0，回退使用预加载 stepId:', preStepId)
        }
        console.log('[TaskView] getTaskDetail 返回:', detailData)
      }
    } catch (detailError) {
      console.warn('[TaskView] getTaskDetail 调用失败:', detailError)
    }
    
    // 构造任务对象
    task.value = {
      taskNo: taskData.taskNo || taskData.TaskNo || taskId.value,
      processName: taskData.processName || taskData.ProcessName || processName.value || '任务详情',
      initiatorName: taskData.initiatorName || taskData.InitiatorName || taskData.initiator || '-',
      approverName: taskData.approverName || taskData.ApproverName || taskData.approver || '-',
      statusName: taskData.statusName || taskData.StatusName || taskData.status || taskData.nodeName || taskData.NodeName || '-',
      priority: taskData.priority || taskData.Priority || 0,
      summary: taskData.summary || taskData.para0 || taskData.Para0 || '-',
      serialNo: taskData.serialNo || taskData.SerialNo || taskData.serialNum || taskData.SerialNum || '-',
      initiateTime: taskData.initiateTime || taskData.createAt || taskData.CreateAt || '',
      operationTime: taskData.operationTime || taskData.operatorAt || taskData.OperatorAt || '',
      sendMail: taskData.sendMail || taskData.SendMail || '',
      agentName: taskData.agentName || taskData.AgentName || '',
      formGuid: taskData.formGuid || taskData.FormGuid || '',
      designerType: taskData.designerType || taskData.DesignerType || taskData.formType || taskData.FormType || '2',
      nodeDescription: taskData.nodeDescription || taskData.NodeDescription || taskData.nodeDesc || taskData.NodeDesc || ''
    }
    
    designerType.value = task.value.designerType
    
    // 提取当前步骤ID（用于审批时传递给后端）
    currentStepId.value = taskData.stepId || taskData.StepId || taskData.pid || taskData.Pid || 0

    // fallback：如果 stepId 为 0，从 getTaskDetail 返回中补充获取
    if (!currentStepId.value && taskId.value) {
      try {
        console.log('[TaskView] stepId 为 0，尝试从 getTaskDetail 补充获取...')
        const detailRes2 = await getTaskDetail(Number(taskId.value))
        const raw2 = detailRes2.data || detailRes2.Data || detailRes2
        const dd = raw2?.Data || raw2  // 解包 { ResultType, Data }
        currentStepId.value = dd?.stepId || dd?.StepId || dd?.currentStepId || 0
        console.log('[TaskView] fallback getTaskDetail 返回 stepId:', currentStepId.value)
      } catch (fbErr) {
        console.warn('[TaskView] fallback getTaskDetail 失败:', fbErr)
      }
    }
    console.log('[TaskView] 当前步骤ID:', currentStepId.value, 'taskId:', taskId.value, 'mode:', mode.value)
    
    // 加载操作按钮配置（从 SYSBPMDLINK 表读取）
    if (mode.value === 'todo' && taskId.value && currentStepId.value) {
      try {
        const btnRes = await getButtonConfig(Number(taskId.value), Number(currentStepId.value))
        // 后端返回 { ResultType: true, Data: [...] }，需解包 Data 字段
        const btnData = btnRes?.data?.Data || btnRes?.Data || btnRes?.data || btnRes || []
        buttonConfigs.value = Array.isArray(btnData) ? btnData : []
        console.log('[TaskView] 按钮配置加载成功:', buttonConfigs.value.length, '个按钮')
      } catch (e) {
        console.warn('[TaskView] 加载按钮配置失败:', e)
        buttonConfigs.value = []
      }
    } else if (mode.value === 'todo') {
      console.warn('[TaskView] 无法加载按钮 - mode=',
        mode.value, 'taskId=', taskId.value, 'currentStepId=', currentStepId.value)
    }
    
    console.log('[TaskView] 构造的任务对象:', task.value)
    
    // 2. 加载审批历史（使用新系统 API）
    try {
      const historyRes = await getTaskHistory(taskId.value)
      const rawHist = historyRes.data || historyRes.Data || historyRes || {}
      const histData = rawHist?.Data || rawHist || []  // 解包 { ResultType, Data }
      histories.value = Array.isArray(histData) ? histData : []
      console.log('[TaskView] 审批历史加载成功:', histories.value.length, '条')
    } catch (e) {
      console.warn('[TaskView] 加载审批历史失败:', e)
      histories.value = []
    }
    
    // 3. 加载表单配置和数据（使用新系统 API）
    let formGuid = taskData.formGuid || taskData.FormGuid || taskData.formDataSetId || taskData.FormDataSetId || task.value.formGuid || ''
    
    console.log('[TaskView] 准备加载表单:', {
      formGuid,
      'taskData.formGuid': taskData.formGuid,
      'taskData.FormGuid': taskData.FormGuid,
      'task.value.formGuid': task.value.formGuid,
      designerType: task.value.designerType
    })
    
    // 4. 填充表单数据（从任务详情中获取，传递原始结构给 OldFormRenderer）
    const savedData = taskData.formData || taskData.FormData
    if (savedData) {
      try {
        let rawData = typeof savedData === 'string' ? JSON.parse(savedData) : savedData
        console.log('[TaskView] 原始表单数据:', rawData)

        // 直接传递原始 main/detail 结构给 OldFormRenderer（由 formdatasrc 属性匹配）
        if (rawData && typeof rawData === 'object') {
          // 将所有 key 转小写以便匹配
          const normalized = {}
          if (rawData.main) {
            normalized.main = {}
            Object.entries(rawData.main).forEach(([tableName, row]) => {
              normalized.main[tableName.toLowerCase()] = row
            })
          }
          if (rawData.detail) {
            normalized.detail = {}
            Object.entries(rawData.detail).forEach(([tableName, rows]) => {
              normalized.detail[tableName.toLowerCase()] = rows
            })
          }
          // 直接赋值给 formDataModel（reactive 对象）
          Object.keys(formDataModel).forEach(k => delete formDataModel[k])
          Object.assign(formDataModel, normalized)
          console.log('[TaskView] 填充表单数据(原始结构) keys:', Object.keys(formDataModel))
        }
      } catch (e) {
        console.warn('[TaskView] 解析表单数据失败:', e)
      }
    }
    
    // 获取流程 GUID
    const processGuid = taskData.flowGuid || taskData.FlowGuid || taskData.processGuid || taskData.ProcessGuid
    const nodeId = taskData.nodeId || taskData.NodeId || 0

    // 加载流程文档和操作文档
    loadAttachDocs(processGuid, nodeId)
    
    // 如果没有 formGuid，尝试通过流程 GUID 获取
    if (!formGuid && processGuid) {
      console.log('[TaskView] 未找到 formGuid，尝试通过流程GUID获取:', processGuid)
      
      try {
        // 尝试调用 getStartMeta
        const { getStartMeta } = await import('../api/task')
        const metaRes = await getStartMeta(processGuid)
        const metaData = metaRes.data || metaRes.Data || metaRes
        
        formGuid = metaData.formGuid || metaData.FormGuid || ''
        if (formGuid) {
          designerType.value = metaData.designerType || metaData.DesignerType || '2'
          task.value.formGuid = formGuid
          task.value.designerType = designerType.value
          console.log('[TaskView] 通过 getStartMeta 获取到 formGuid:', formGuid)
        }
      } catch (metaError) {
        console.warn('[TaskView] getStartMeta 调用失败:', metaError)
        
        // 尝试通过 getFormsByFlow 获取表单列表
        try {
          const { getFormsByFlow } = await import('../api/form')
          const formsRes = await getFormsByFlow(processGuid)
          const forms = formsRes.data || formsRes.Data || formsRes || []
          
          console.log('[TaskView] getFormsByFlow 返回表单列表:', forms)
          
          if (forms.length > 0) {
            const firstForm = forms[0]
            formGuid = firstForm.guid || firstForm.Guid || firstForm.formGuid || firstForm.FormGuid || ''
            designerType.value = firstForm.designerType || firstForm.DesignerType || firstForm.type || firstForm.Type || '2'
            task.value.formGuid = formGuid
            task.value.designerType = designerType.value
            console.log('[TaskView] 使用第一个表单:', { formGuid, designerType: designerType.value })
          }
        } catch (formListError) {
          console.warn('[TaskView] getFormsByFlow 调用失败:', formListError)
        }
      }
    }
    
    // 加载表单配置
    if (formGuid) {
      try {
        const { getFormById } = await import('../api/form')
        const formRes = await getFormById(formGuid)
        const formData = formRes.data || formRes.Data || formRes
        
        formContent.value = formData.formContent || formData.FormContent || ''
        jsContent.value = formData.jsContent || formData.JSContent || ''
        
        console.log('[TaskView] 表单配置加载成功, contentLength:', formContent.value?.length)
        
        // 智能判断设计器类型：如果内容以 < 开头（HTML标签），则为旧设计器
        if (formContent.value) {
          const trimmedContent = formContent.value.trim()
          if (trimmedContent.startsWith('<') || trimmedContent.startsWith('<!')) {
            // HTML内容，旧设计器
            designerType.value = '1'
            console.log('[TaskView] 检测到HTML内容，设置为旧设计器')
          } else {
            // 尝试解析JSON，新设计器
            try {
              const parsed = JSON.parse(formContent.value)
              if (Array.isArray(parsed)) {
                designerType.value = '2'
                formControls.value = parsed
                console.log('[TaskView] 检测到JSON数组，设置为新设计器，解析了', formControls.value.length, '个控件')
              } else {
                // 不是数组，可能是旧设计器的其他格式
                designerType.value = '1'
                console.log('[TaskView] JSON解析成功但不是数组，设置为旧设计器')
              }
            } catch (e) {
              // JSON解析失败，当作旧设计器HTML处理
              designerType.value = '1'
              console.log('[TaskView] JSON解析失败，设置为旧设计器')
            }
          }
        }
        
        task.value.designerType = designerType.value
        console.log('[TaskView] 最终设计器类型:', designerType.value)
      } catch (e) {
        console.warn('[TaskView] 加载表单配置失败:', e)
      }
    } else {
      console.warn('[TaskView] 未找到 formGuid，无法加载表单')
    }
  } catch (e) {
    console.error('[TaskView] 加载任务详情失败:', e)
    error.value = e.message || '加载失败，请稍后重试'
  } finally {
    loading.value = false
  }
}

// 表单数据更新（来自 OldFormRenderer 的 formdatasrc 格式）
const onFormDataUpdate = (data) => {
  // data 格式: { formdatasrc: "table.field", tableName, fieldName, value }
  if (data.tableName && data.fieldName) {
    const tableName = data.tableName.toLowerCase()
    // 更新到 main 结构（常规控件都在 main 中）
    if (!formDataModel.main) formDataModel.main = {}
    if (!formDataModel.main[tableName]) formDataModel.main[tableName] = {}
    formDataModel.main[tableName][data.fieldName] = data.value
  }
}

// 同意审批
// 获取按钮图标
const getButtonIcon = (btn) => {
  const name = (btn.name || btn.actionLinkName || '').toLowerCase()
  const linkId = btn.actionLinkId || 0
  // 参考旧系统 ActionHandler.ashx linkId 映射
  if (linkId === 10003 || name.includes('拒绝') || name.includes('退回')) return 'fas fa-times'
  if (linkId === 10000 || name.includes('保存') || name.includes('暂存')) return 'fas fa-save'
  if (linkId === 10006 || name.includes('转办') || name.includes('委托')) return 'fas fa-share'
  if (linkId === 10020 || name.includes('撤回') || name.includes('撤销')) return 'fas fa-undo'
  if (linkId === 10012 || name.includes('批量')) return 'fas fa-check-double'
  if (linkId === 10004 || name.includes('退回重填')) return 'fas fa-undo-alt'
  if (linkId === 10007 || name.includes('直送')) return 'fas fa-paper-plane'
  if (linkId === 10013 || name.includes('跳转') || name.includes('调度')) return 'fas fa-random'
  if (linkId === 10014 || linkId === 10025 || name.includes('加签')) return 'fas fa-plus-circle'
  if (linkId === 10018 || name.includes('询问')) return 'fas fa-question-circle'
  if (linkId === 10001 || name.includes('公开')) return 'fas fa-eye'
  if (linkId === 10010 || name.includes('暂停')) return 'fas fa-pause'
  if (linkId === 10011 || name.includes('恢复')) return 'fas fa-play'
  // 默认同意/提交类按钮
  return 'fas fa-check'
}

// 统一按钮点击处理（参考旧系统 ActionHandler.ashx linkId 映射）
const handleButtonClick = async (btn) => {
  if (mode.value !== 'todo') return
  
  const linkId = btn.actionLinkId || 0
  const actionName = btn.actionLinkName || btn.name || ''
  
  // 特殊处理：转办/委托需要弹出选择用户对话框
  if (linkId === 10006 || actionName.includes('转办') || actionName.includes('委托')) {
    showForwardDialog.value = true
    forwardSearch.value = ''
    forwardSelectedUser.value = null
    try {
      const res = await getUserList()
      const data = res.data || res.Data || res || []
      allUsers.value = Array.isArray(data) ? data : []
    } catch (e) {
      console.error('加载用户列表失败:', e)
      allUsers.value = []
    }
    return
  }
  
  // 特殊处理：拒绝/退回需要填写审批意见
  if ((linkId === 10003 || actionName.includes('拒绝') || actionName.includes('退回')) && 
      (!comment.value || comment.value.trim() === '')) {
    $message.warning('请填写审批意见')
    return
  }
  
  // 根据 linkId 映射到后端 Action
  let action = 'agree' // 默认同意
  if (linkId === 10003 || actionName.includes('拒绝')) action = 'reject'
  else if (linkId === 10000 || actionName.includes('保存') || actionName.includes('暂存')) action = 'save'
  else if (linkId === 10020 || actionName.includes('撤回') || actionName.includes('撤销')) action = 'withdraw'
  else if (linkId === 10004 || actionName.includes('退回重填')) action = 'reject'
  else if (linkId === 10007 || actionName.includes('直送')) action = 'agree'
  else if (linkId === 10012 || actionName.includes('批量')) action = 'agree'
  
  submitting.value = true
  submittingActionId.value = btn.id
  
  try {
    const payload = {
      taskId: taskId.value,
      stepId: currentStepId.value,
      action: action,
      actionLinkName: actionName,
      actionLinkId: linkId,
      memo: comment.value,
      formData: JSON.stringify(formDataModel)
    }
    
    const res = await approveTask(payload)
    const result = res.data || res.Data || res || {}
    
    // 根据后端返回结果显示消息
    let msg = '操作成功！'
    if (result.resultState === 'finished') msg = '流程已完成！'
    else if (result.resultState === 'rejected') msg = '已退回！'
    else if (result.resultState === 'saved') msg = '已暂存！'
    else if (result.resultState === 'withdrawn') msg = '已撤销！'
    else if (result.message) msg = result.message
    
    $message.success(msg)
    
    // 返回待办列表
    router.push('/pending-tasks')
  } catch (e) {
    console.error('操作失败:', e)
    $message.error('操作失败: ' + (e.message || '未知错误'))
  } finally {
    submitting.value = false
    submittingActionId.value = 0
  }
}

// 转办提交
const doForwardSubmit = async () => {
  if (!forwardSelectedUser.value) {
    $message.warning('请选择转办人员')
    return
  }
  
  forwardSubmitting.value = true
  try {
    const payload = {
      taskId: taskId.value,
      stepId: currentStepId.value,
      action: 'forward',
      targetAccount: forwardSelectedUser.value.Account || forwardSelectedUser.value.account,
      memo: comment.value,
      formData: JSON.stringify(formDataModel)
    }
    
    await forwardTask(payload)
    $message.success('转办成功！')
    showForwardDialog.value = false
  } catch (e) {
    console.error('转办失败:', e)
    $message.error('转办失败: ' + (e.message || '未知错误'))
  } finally {
    forwardSubmitting.value = false
  }
}

// 过滤用户列表
const filteredUsers = computed(() => {
  if (!forwardSearch.value) return allUsers.value
  const kw = forwardSearch.value.toLowerCase()
  return allUsers.value.filter(u => {
    const name = (u.DisplayName || u.displayName || '').toLowerCase()
    const account = (u.Account || u.account || '').toLowerCase()
    return name.includes(kw) || account.includes(kw)
  })
})

// 取回任务
const doRetrieve = async () => {
  if (mode.value !== 'processed') return
  
  if (!await $confirm('确定要取回该任务吗？')) return
  
  submitting.value = true
  
  try {
    const res = await retrieveTask({
      taskId: taskId.value,
      stepId: currentStepId.value || 0
    })
    if (res && res.resultType) {
      $message.success('取回成功')
      router.push('/pending-tasks')
    } else {
      $message.error((res && res.message) || '取回失败')
    }
  } catch (e) {
    console.error('取回失败:', e)
    $message.error('取回失败: ' + (e.message || '未知错误'))
  } finally {
    submitting.value = false
  }
}

// 监听 taskId 变化，切换任务时重新加载数据
watch(taskId, (newId, oldId) => {
  if (newId && newId !== oldId) {
    console.log('[TaskView] taskId 变化，重新加载:', newId, '(旧值:', oldId, ')')
    // 重置所有数据
    histories.value = []
    formContent.value = ''
    jsContent.value = ''
    formControls.value = []
    Object.keys(formDataModel).forEach(key => delete formDataModel[key])
    task.value = {}
    currentStepId.value = 0
    comment.value = ''
    error.value = ''
    loadTaskDetail()
  }
})

onMounted(() => {
  console.log('[TaskView] onMounted, taskId:', taskId.value)
  loadTaskDetail()
})

const openPrint = () => {
  if (taskId.value) {
    window.open(`${window.location.origin}/#/print/${taskId.value}`, '_blank', 'width=900,height=700')
  }
}
</script>

<style scoped>
.view-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #f5f5f5;
}

.loading-state,
.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  gap: 8px;
  color: #999;
  flex: 1;
}

.loading-state i,
.error-state i {
  font-size: 24px;
}

.error-state {
  color: #d9534f;
}

/* 旧系统布局结构 */
.old-system-layout {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* 第一行: 任务信息标题栏 - 绿色基调 */
.task-header-bar {
  padding: 10px 20px;
  border-bottom: 1px solid #e0e4e8;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: #fff;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.task-title-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 4px;
}

.task-title {
  font-size: 16px;
  font-weight: 600;
  color: #fff;
  margin: 0;
}

.task-status {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 16px;
  font-size: 11px;
  font-weight: 600;
  background: rgba(255,255,255,0.2);
  color: #fff;
  backdrop-filter: blur(10px);
}

.btn-print {
  margin-left: auto;
  padding: 5px 14px;
  border: 1px solid rgba(255,255,255,0.3);
  background: rgba(255,255,255,0.15);
  color: #fff;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  transition: all 0.2s;
}
.btn-print:hover { background: rgba(255,255,255,0.25); }

.status-processing { background: rgba(37, 99, 235, 0.9); }
.status-done { background: rgba(22, 163, 74, 0.9); }
.status-rejected { background: rgba(220, 38, 38, 0.9); }

.task-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  font-size: 12px;
  color: rgba(255,255,255,0.95);
}

.task-meta span {
  display: flex;
  align-items: center;
  gap: 6px;
}

.task-meta i { 
  color: rgba(255,255,255,0.8); 
  font-size: 12px;
}

/* 单行显示任务信息 */
.task-meta-single-line {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 12px;
  color: rgba(255,255,255,0.95);
  overflow-x: auto;
  white-space: nowrap;
  padding-bottom: 2px;
}

.task-meta-single-line::-webkit-scrollbar {
  height: 4px;
}

.task-meta-single-line::-webkit-scrollbar-track {
  background: rgba(255,255,255,0.1);
  border-radius: 2px;
}

.task-meta-single-line::-webkit-scrollbar-thumb {
  background: rgba(255,255,255,0.3);
  border-radius: 2px;
}

.meta-item {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
}

.meta-item i {
  color: rgba(255,255,255,0.8);
  font-size: 12px;
  flex-shrink: 0;
}

.meta-divider {
  color: rgba(255,255,255,0.5);
  font-weight: 300;
}

/* 第二行: 左右分栏主内容区 */
.main-content-layout {
  flex: 1;
  display: flex;
  gap: 12px;
  padding: 12px;
  overflow: hidden;
  background: #f5f7fa;
}

/* 左侧面板 (75% 宽度) */
.left-panel {
  flex: 0 0 75%;
  display: flex;
  flex-direction: column;
  gap: 12px;
  overflow: hidden;
  min-height: 0;
}

/* 右侧面板 (25% 宽度) */
.right-panel {
  flex: 0 0 23%;
  display: flex;
  flex-direction: column;
  gap: 12px;
  overflow-y: auto;
}

/* 表单区域 - 占据主要空间 */
.form-section {
  flex: 1;
  min-height: 0;
  background: #fff;
  border-radius: 6px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.08);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  background: linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%);
  border-bottom: 2px solid #e5e7eb;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  flex-shrink: 0;
}

.section-header i {
  margin-right: 8px;
  color: #667eea;
  font-size: 13px;
}

.section-header.collapsible {
  cursor: pointer;
  user-select: none;
  transition: background 0.2s;
}

.section-header.collapsible:hover {
  background: linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%);
}

.header-left {
  display: flex;
  align-items: center;
  gap: 6px;
}

.count-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  background: #667eea;
  color: #fff;
  border-radius: 9px;
  font-size: 10px;
  font-weight: 600;
}

.form-content {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  background: #fff;
}

/* 审批历史区域 - 默认折叠，紧凑显示 */
.history-section {
  flex-shrink: 0;
  background: #fff;
  border-radius: 6px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.08);
  max-height: 280px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.history-content {
  padding: 10px 14px;
  overflow-y: auto;
  background: #fafbfc;
}

.history-table-container {
  overflow-x: auto;
  border-radius: 4px;
  border: 1px solid #e5e7eb;
  background: #fff;
}

.history-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
  background: #fff;
}

.history-table thead {
  background: linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%);
}

.history-table th {
  padding: 8px 10px;
  text-align: center;
  border-bottom: 2px solid #e5e7eb;
  font-weight: 600;
  color: #374151;
  white-space: nowrap;
  font-size: 12px;
}

.history-table td {
  padding: 6px 10px;
  border-bottom: 1px solid #f3f4f6;
  color: #6b7280;
  text-align: center;
  font-size: 12px;
}

.history-table td.text-left {
  text-align: left;
}

.history-table td.text-center {
  text-align: center;
}

.history-table tbody tr:hover {
  background: #f9fafb;
}

.history-table tbody tr:last-child td {
  border-bottom: none;
}

.empty-history {
  text-align: center;
  padding: 16px 20px;
  color: #9ca3af;
  font-size: 12px;
  background: #fafbfc;
  border: 1px dashed #e5e7eb;
  border-radius: 4px;
}

/* 右侧操作卡片 */
.side-card {
  background: #fff;
  border-radius: 6px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.08);
  overflow: hidden;
  flex-shrink: 0;
}

/* 基本信息和审批操作卡片紧凑显示 */
.side-card:first-child .card-body,
.side-card:nth-child(2) .card-body {
  padding: 10px 14px;
}

.card-header {
  display: flex;
  align-items: center;
  padding: 10px 14px;
  background: linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%);
  border-bottom: 2px solid #e5e7eb;
  font-size: 12px;
  font-weight: 600;
  color: #374151;
}

.card-header i {
  margin-right: 6px;
  color: #667eea;
  font-size: 12px;
}

.card-body {
  padding: 12px 14px;
}

/* 信息行 */
.info-row {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin-bottom: 8px;
  font-size: 12px;
}

.info-row:last-child {
  margin-bottom: 0;
}

.info-label {
  color: #6b7280;
  font-weight: 500;
  white-space: nowrap;
  min-width: 50px;
}

.info-value {
  color: #111827;
  font-weight: 500;
  flex: 1;
  word-break: break-word;
}

.priority-tag {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
}

.priority-high { background: #fee2e2; color: #dc2626; }
.priority-medium { background: #dbeafe; color: #2563eb; }
.priority-low { background: #d1fae5; color: #059669; }

/* 表单组 */
.form-group {
  margin-bottom: 10px;
}

.form-group:last-child {
  margin-bottom: 0;
}

.form-label {
  display: block;
  font-size: 11px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 4px;
}

.form-textarea {
  width: 100%;
  padding: 6px 8px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 12px;
  outline: none;
  background: #fff;
  resize: vertical;
  font-family: inherit;
  transition: all 0.2s;
  box-sizing: border-box;
}

.form-textarea:focus {
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

/* 垂直排列按钮 */
.action-buttons-vertical {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/* 空白提示 */
.empty-tip {
  text-align: center;
  padding: 12px 10px;
  color: #9ca3af;
  font-size: 11px;
  background: #fafbfc;
  border: 1px dashed #e5e7eb;
  border-radius: 4px;
}

.doc-item {
  display: flex;
  align-items: center;
  padding: 4px 0;
  font-size: 12px;
}

.doc-link {
  color: #1890ff;
  text-decoration: none;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.doc-link:hover {
  text-decoration: underline;
}

.doc-size {
  color: #9ca3af;
  font-size: 11px;
  margin-left: 8px;
  white-space: nowrap;
}

/* 节点说明 */
.node-description {
  padding: 8px 10px;
  color: #374151;
  font-size: 12px;
  line-height: 1.5;
  background: #fafbfc;
  border-radius: 4px;
  min-height: 36px;
  white-space: pre-wrap;
  word-break: break-word;
}

/* 审批路线 */
.route-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.route-node {
  padding: 6px 8px;
  background: #fafbfc;
  border-left: 3px solid #667eea;
  border-radius: 3px;
  transition: all 0.2s;
}

.route-node:hover {
  background: #f3f4f6;
  border-left-color: #4f46e5;
}

.route-node-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
}

.route-node-name {
  font-size: 13px;
  font-weight: 600;
  color: #374151;
}

.route-current-badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  background: #667eea;
  color: #fff;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
}

.route-node-users {
  font-size: 12px;
  color: #6b7280;
  line-height: 1.5;
}

/* 调试面板 */
.debug-panel {
  margin-bottom: 12px;
  background: #fffbeb;
  border: 1px solid #fbbf24;
  border-radius: 6px;
  padding: 10px;
  font-size: 11px;
}

.debug-panel summary {
  cursor: pointer;
  font-weight: 600;
  color: #92400e;
  user-select: none;
}

.debug-content {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid #fde68a;
  color: #78350f;
}

.debug-content p {
  margin: 4px 0;
}

.empty-form-hint {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  gap: 8px;
  color: #9ca3af;
  background: #fafbfc;
  border-radius: 8px;
  border: 2px dashed #e5e7eb;
}

.empty-form-hint i {
  font-size: 32px;
  color: #d1d5db;
}

.debug-hint {
  font-size: 11px;
  color: #9ca3af;
  margin-top: 8px;
}

/* 通用按钮 */
.btn {
  padding: 6px 14px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  color: #fff;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

.btn-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 4px;
}

.btn-primary:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(102, 126, 234, 0.3);
}

/* 滚动条美化 */
.form-content::-webkit-scrollbar,
.history-content::-webkit-scrollbar,
.right-panel::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

.form-content::-webkit-scrollbar-track,
.history-content::-webkit-scrollbar-track,
.right-panel::-webkit-scrollbar-track {
  background: #f3f4f6;
  border-radius: 3px;
}

.form-content::-webkit-scrollbar-thumb,
.history-content::-webkit-scrollbar-thumb,
.right-panel::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

.form-content::-webkit-scrollbar-thumb:hover,
.history-content::-webkit-scrollbar-thumb:hover,
.right-panel::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* 响应式调整 */
@media (max-width: 1200px) {
  .main-content-layout {
    flex-direction: column;
  }
  
  .left-panel,
  .right-panel {
    flex: 1 1 auto;
    width: 100%;
  }
  
  .right-panel {
    max-height: 600px;
  }
}

/* 表单区域无标题样式 */
.form-content.no-header {
  padding: 0;
  background: transparent;
}

/* 审批按钮网格 - 自适应宽度流式布局 */
.action-buttons-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 10px;
}

.btn-action {
  padding: 6px 10px;
  border: none;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  color: #fff;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  white-space: nowrap;
  flex: 0 0 auto;
}

.btn-action:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* 操作按钮样式 - 基于按钮类型 */
.btn-primary {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(16, 185, 129, 0.3);
}

.btn-danger {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
}

.btn-danger:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(239, 68, 68, 0.3);
}

.btn-warning {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
}

.btn-warning:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(245, 158, 11, 0.3);
}

.btn-info {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
}

.btn-info:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(59, 130, 246, 0.3);
}

/* 兼容旧类名 */
.btn-approve {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
}

.btn-approve:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(16, 185, 129, 0.3);
}

.btn-reject {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
}

.btn-reject:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(239, 68, 68, 0.3);
}

.btn-save {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
}

.btn-save:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(245, 158, 11, 0.3);
}

.btn-forward {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
}

.btn-forward:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(59, 130, 246, 0.3);
}

.btn-full {
  width: 100%;
  padding: 6px 10px;
  border-radius: 4px;
}

/* 转办对话框 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.forward-dialog {
  background: #fff;
  border-radius: 8px;
  width: 420px;
  max-height: 500px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 30px rgba(0,0,0,0.15);
}

.dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  border-bottom: 1px solid #eee;
  font-size: 15px;
  font-weight: 600;
  color: #333;
}

.dialog-close {
  background: none;
  border: none;
  font-size: 22px;
  color: #999;
  cursor: pointer;
  line-height: 1;
  padding: 0;
}

.dialog-close:hover {
  color: #333;
}

.dialog-body {
  padding: 16px 20px;
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.forward-search-input {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  font-size: 13px;
  outline: none;
  box-sizing: border-box;
  margin-bottom: 10px;
}

.forward-search-input:focus {
  border-color: #1890ff;
  box-shadow: 0 0 0 2px rgba(24,144,255,0.1);
}

.forward-user-list {
  flex: 1;
  overflow-y: auto;
  max-height: 280px;
  border: 1px solid #f0f0f0;
  border-radius: 6px;
}

.forward-user-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  cursor: pointer;
  border-bottom: 1px solid #f5f5f5;
  transition: background 0.15s;
}

.forward-user-item:last-child {
  border-bottom: none;
}

.forward-user-item:hover {
  background: #f0f8ff;
}

.forward-user-item.selected {
  background: #e6f7ff;
  border-left: 3px solid #1890ff;
}

.forward-user-name {
  font-size: 13px;
  color: #333;
  font-weight: 500;
}

.forward-user-account {
  font-size: 12px;
  color: #999;
}

.forward-empty {
  padding: 30px;
  text-align: center;
  color: #999;
  font-size: 13px;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 12px 20px;
  border-top: 1px solid #eee;
}

.btn-cancel {
  padding: 7px 18px;
  border: 1px solid #d9d9d9;
  background: #fff;
  border-radius: 5px;
  font-size: 13px;
  cursor: pointer;
  color: #666;
}

.btn-cancel:hover {
  border-color: #1890ff;
  color: #1890ff;
}

.btn-submit {
  padding: 7px 18px;
  border: none;
  background: #1890ff;
  color: #fff;
  border-radius: 5px;
  font-size: 13px;
  cursor: pointer;
}

.btn-submit:hover:not(:disabled) {
  background: #177ddc;
}

.btn-submit:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
